import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import DisclaimerStep from './DisclaimerStep';
import PersonalDataStep from './PersonalDataStep';
import PromptGenerator from '../generation/PromptGenerator';
import TrainingAndGenerator from './TrainingAndGenerator';
import AppHeader from '../shared/AppHeader';
import { API_URL } from '../../config';

const CreateWizard = ({ onBackToDashboard }) => {
  // Read step from URL parameter (e.g., ?step=2) or default to 1
  const urlParams = new URLSearchParams(window.location.search);
  const stepFromUrl = parseInt(urlParams.get('step') || '1');
  const [currentStep, setCurrentStep] = useState(stepFromUrl);
  const [userCredits, setUserCredits] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false); // Hamburger-Menü State
  // Snapshot 3.0 — schlankes Wizard-State
  const [wizardData, setWizardData] = useState({
    // Step 1: Disclaimer
    disclaimerAccepted: false,
    age18Plus: false,
    termsAccepted: false,

    // Step 2: Avatar-Daten
    name: '',
    avatarType: 'full_body', // einheitlicher Flow — Auswahl entfällt, immer Vollkörper-Avatar
    slots: {},               // { slotName: {photoId, scores, fileName, previewUrl} }
  });
  
  // Load user credits
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('snapshot_user') || '{}');
    const userId = user.id;
    
    if (userId) {
      fetch(`${API_URL}/api/user/credits`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('snapshot_token')}` }
      })
        .then(res => res.json())
        .then(data => setUserCredits(data.credits || 0))
        .catch(err => console.error('Error loading credits:', err));
    }
  }, []);

  // Restore wizard data from localStorage (after Stripe checkout)
  useEffect(() => {
    const savedWizardData = localStorage.getItem('snapshot_wizard_data');
    if (savedWizardData) {
      try {
        const parsedData = JSON.parse(savedWizardData);
        console.log('✅ Wizard-Daten aus localStorage wiederhergestellt', parsedData);
        setWizardData(prev => ({
          ...prev,
          ...parsedData
        }));
        // Clear the saved data after restoring
        localStorage.removeItem('snapshot_wizard_data');
      } catch (error) {
        console.error('❌ Fehler beim Wiederherstellen der Wizard-Daten:', error);
      }
    }
  }, []);

  useEffect(() => {
    const jumpToTop = () => window.scrollTo(0, 0);
    jumpToTop();
    const raf = requestAnimationFrame(jumpToTop);
    const timeout = setTimeout(jumpToTop, 80);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timeout);
    };
  }, [currentStep]);

  const updateWizardData = (field, value) => {
    setWizardData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const nextStep = () => {
    const newStep = Math.min(currentStep + 1, 3); // Now 3 steps: Richtlinien → PersonalData → Training
    setCurrentStep(newStep);
    // Save current step to localStorage
    localStorage.setItem('wizard_current_step', newStep.toString());
  };

const prevStep = () => {
    const newStep = Math.max(currentStep - 1, 1);
    setCurrentStep(newStep);
    // Save current step to localStorage
    localStorage.setItem('wizard_current_step', newStep.toString());
  };
  
  const handleBackToDashboard = () => {
    // Always go back to Dashboard
    window.location.href = '/';
  };

  // Fehler bei der Avatar-Erstellung: zurück zum Foto-Schritt, Eingaben bleiben
  // erhalten, Fehlermeldung wird dort als Banner angezeigt.
  const goBackToPhotosWithError = (message) => {
    updateWizardData('createError', message || 'Es ist ein Fehler aufgetreten.');
    setCurrentStep(2);
    localStorage.setItem('wizard_current_step', '2');
  };

  const goBackToLanding = () => {
    window.location.href = '/';
  };
  
  const goBackToDashboard = () => {
    // Always redirect to Dashboard
    window.location.href = '/';
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <DisclaimerStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={nextStep}
            onCancel={goBackToDashboard}
          />
        );
      case 2:
        return (
          <PersonalDataStep
            data={wizardData}
            updateData={updateWizardData}
            onNext={nextStep} // Go to next step (Training)
            onBack={prevStep}
          />
        );
      case 3:
        return (
          <TrainingAndGenerator 
            wizardData={wizardData}
            onBackToDashboard={handleBackToDashboard}
            onBackToPhotos={goBackToPhotosWithError}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-black grain-texture">
      {/* Shared Header Component mit Hamburger */}
      <AppHeader menuOpen={menuOpen} onMenuToggle={() => setMenuOpen(!menuOpen)} />
      
      {/* Overlay - Schließt Menü bei Klick außerhalb */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30"
          onClick={() => setMenuOpen(false)}
        ></div>
      )}
      
      {/* Side Menu - GLEICH WIE BEI ID CARD - SCROLLBAR */}
      <div className={`fixed top-0 right-0 h-full w-80 bg-black/95 backdrop-blur-xl border-l border-white/10 transform transition-transform duration-300 z-40 ${menuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <nav className="flex flex-col p-8 space-y-6 mt-20 h-full overflow-y-auto pb-24">
          
          {/* User Info im Menü */}
          <div className="pb-4 border-b border-white/10 mb-2">
            <p className="text-sm text-white/50 mb-1">{JSON.parse(localStorage.getItem('snapshot_user') || '{}').name || 'User'}</p>
            <p className="text-xs text-white/40 mb-3">{JSON.parse(localStorage.getItem('snapshot_user') || '{}').email}</p>
            <div className="flex items-center justify-between">
              <span className="text-sm text-[#10B981] font-medium">{Number((userCredits || 0).toFixed(2))} SNAPs</span>
              <button 
                onClick={() => {
                  setMenuOpen(false);
                  window.location.href = '/credits';
                }}
                className="text-xs text-white/60 hover:text-[#10B981] transition-colors"
              >
                + kaufen
              </button>
            </div>
          </div>
          
          <button 
            onClick={() => {
              setMenuOpen(false);
              goBackToDashboard();
            }}
            className="text-white hover:text-[#10B981] transition-colors text-lg font-medium text-left"
          >
            🏠 Meine ID Card
          </button>
          
          <button 
            onClick={() => {
              setMenuOpen(false);
              localStorage.removeItem('snapshot_token');
              localStorage.removeItem('snapshot_user');
              localStorage.removeItem('snapshot_beta_access');
              window.location.href = '/welcome';
            }}
            className="text-white/60 hover:text-white transition-colors text-sm text-left"
          >
            🚪 Abmelden
          </button>
          <div className="border-t border-white/10 my-2"></div>
          <Link to="/was-ist-snapshot" onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white transition-colors text-sm">
            Was ist SNAPSHOT
          </Link>
          <Link to="/impressum" onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white transition-colors text-sm">
            Impressum
          </Link>
          <Link to="/datenschutz" onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white transition-colors text-sm">
            Datenschutz
          </Link>
          <Link to="/agb" onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white transition-colors text-sm">
            AGB
          </Link>
          <Link to="/kontakt" onClick={() => setMenuOpen(false)} className="text-white/60 hover:text-white transition-colors text-sm">
            Kontakt
          </Link>
          <button onClick={() => window.dispatchEvent(new Event('openCookieSettings'))} className="text-white/60 hover:text-white transition-colors text-sm text-left">
            Cookie-Einstellungen
          </button>
        </nav>
      </div>

      {/* Main Content with Top Padding */}
      <div className="pt-16 pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          
          {/* iOS-style Progress Dots - 2 STEPS */}
          {currentStep < 3 && (
            <div className="mb-8 flex flex-col items-center gap-3">
              <div className="flex items-center gap-2">
                {[1, 2].map((step) => (
                  <div
                    key={step}
                    className={`transition-all duration-300 ${
                      step === currentStep
                        ? 'w-8 h-2 bg-[#10B981] rounded-full glow-green-soft'
                        : step < currentStep
                        ? 'w-2 h-2 bg-[#10B981]/60 rounded-full'
                        : 'w-2 h-2 bg-white/20 rounded-full'
                    }`}
                  />
                ))}
              </div>
              <p className="text-white/50 text-xs">Schritt {currentStep} von 3</p>
            </div>
          )}

          {/* Render Current Step */}
          {renderStep()}
        </div>
      </div>
    </div>
  );
};

export default CreateWizard;
